/**
 * Passive UX signals: browser adapter (Doug LOCK 2026-10-03). No UI.
 *
 * Wires the core (uxSignalsCore.js) to the page:
 *   - route changes            -> setScreen(pattern, subview) from the app's router
 *   - first data               -> first Supabase REST / edge response after the route change
 *                                 (PerformanceObserver 'resource'; no fetch wrapping)
 *   - abandon / time-to-complete -> a form on the page: first input = flow start,
 *                                 submit = primary action
 *   - rage taps                -> click (capture, passive)
 *   - JS errors                -> window 'error' / 'unhandledrejection'
 * and posts batches through the existing app-feedback edge function
 * (type 'batch' -> public.app_events). Signed-in only: nothing is queued
 * without a Supabase session, and a batch is dropped if the session is gone at
 * send time. Every global is injectable for node:test.
 */
import { createBatcher, createUxSignals } from './uxSignalsCore.js';

const DATA_PATH_RE = /\/(rest|graphql|functions|storage)\/v1\//;

function targetKey(el) {
  if (!el || typeof el.closest !== 'function') return null;
  const tagged = el.closest('[data-testid]');
  if (tagged?.getAttribute) return `testid:${tagged.getAttribute('data-testid')}`;
  const control = el.closest('button,a,[role="button"],input,select,textarea,label,summary');
  return control?.tagName ? control.tagName.toLowerCase() : null;
}

export function installWebUxSignals({
  app,
  supabase,
  supabaseUrl,
  anonKey,
  win = typeof window === 'undefined' ? null : window,
  doc = typeof document === 'undefined' ? null : document,
  perf = typeof performance === 'undefined' ? null : performance,
  fetchImpl = typeof fetch === 'undefined' ? null : (...a) => fetch(...a),
  PerformanceObserverImpl = typeof PerformanceObserver === 'undefined' ? null : PerformanceObserver,
  flushMs = 30_000,
  onScreen = null,
  // First screen timed from navigation start (a full page load). Pass false when
  // installing after a client-side route change.
  firstScreenFromNavigationStart = true,
  coreOptions = {},
} = {}) {
  if (!win || !doc || !supabase?.auth || !supabaseUrl || !anonKey || !fetchImpl) {
    return { setScreen() {}, setSubview() {}, flush: async () => true, destroy() {}, ux: null };
  }

  let signedIn = false;
  const endpoint = `${String(supabaseUrl).replace(/\/+$/, '')}/functions/v1/app-feedback`;

  async function accessToken() {
    try {
      const { data } = await supabase.auth.getSession();
      return data?.session?.access_token || null;
    } catch {
      return null;
    }
  }

  const batcher = createBatcher({
    async send(batch, { keepalive = false } = {}) {
      const token = await accessToken();
      if (!token) return true; // signed out since queueing: drop, never send as anon
      const res = await fetchImpl(endpoint, {
        method: 'POST',
        keepalive,
        headers: { 'Content-Type': 'application/json', apikey: anonKey, Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'batch',
          app,
          platform: 'web',
          events: batch.map(({ __retried, ...e }) => e),
        }),
      });
      return Boolean(res?.ok);
    },
  });

  const ux = createUxSignals({
    app,
    ...coreOptions,
    isSignedIn: () => signedIn,
    sink: (event) => {
      if (batcher.push(event)) batcher.flush();
    },
  });

  accessToken().then((t) => { signedIn = Boolean(t); });
  const authSub = supabase.auth.onAuthStateChange?.((_evt, session) => {
    signedIn = Boolean(session?.access_token);
  });

  const timeOrigin = perf?.timeOrigin ?? (Date.now() - (perf?.now?.() ?? 0));
  let routeStartPerf = 0; // first screen: from navigation start
  let first = Boolean(firstScreenFromNavigationStart);

  const listeners = [];
  const on = (target, type, fn, opts) => {
    target.addEventListener(type, fn, opts);
    listeners.push(() => target.removeEventListener(type, fn, opts));
  };

  on(doc, 'click', (e) => ux.tap(e.clientX ?? 0, e.clientY ?? 0, targetKey(e.target)), { capture: true, passive: true });
  on(doc, 'input', (e) => { if (e.target?.closest?.('form')) ux.flowStart('form'); }, { capture: true, passive: true });
  on(doc, 'submit', () => ux.flowComplete('form'), { capture: true });
  on(win, 'error', (e) => ux.error(e?.error || e?.message || 'Error'));
  on(win, 'unhandledrejection', (e) => ux.error(e?.reason || 'Unhandled rejection'));
  // Tab hidden: just flush (switching tabs mid-form is not an abandon).
  // Page unloading: the current screen is left, then flush.
  on(doc, 'visibilitychange', () => { if (doc.visibilityState === 'hidden') batcher.flush({ keepalive: true }); });
  on(win, 'pagehide', () => {
    ux.background();
    batcher.flush({ keepalive: true });
  });

  let observer = null;
  if (PerformanceObserverImpl) {
    try {
      observer = new PerformanceObserverImpl((list) => {
        for (const entry of list.getEntries()) {
          const name = String(entry.name || '');
          if (!DATA_PATH_RE.test(name) || name.includes('/functions/v1/app-feedback')) continue;
          if (entry.startTime < routeStartPerf) continue;
          ux.dataLoaded(timeOrigin + entry.responseEnd);
          break;
        }
      });
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      observer = null;
    }
  }

  const timer = win.setInterval ? win.setInterval(() => batcher.flush(), flushMs) : null;

  return {
    ux,
    /** Call on every route change with the route PATTERN (never the raw path). */
    setScreen(screen, subview = null) {
      routeStartPerf = first ? 0 : (perf?.now?.() ?? 0);
      ux.screen(screen, { subview, at: first ? timeOrigin : null });
      first = false;
      if (onScreen) {
        try { onScreen(screen); } catch { /* tagging is best-effort */ }
      }
    },
    setSubview(subview) {
      ux.subview(subview);
    },
    flush: (opts) => batcher.flush(opts),
    /** Unmount. By default the current screen counts as left and the queue is flushed (keepalive). */
    destroy({ flush = true } = {}) {
      if (flush) {
        ux.background();
        batcher.flush({ keepalive: true });
      }
      listeners.splice(0).forEach((off) => off());
      observer?.disconnect?.();
      if (timer && win.clearInterval) win.clearInterval(timer);
      authSub?.data?.subscription?.unsubscribe?.();
    },
  };
}
